import { withTrailingSlash } from 'ufo'
import type { ComputedRef, Ref } from 'vue'
import type { SearchEngineEventItem } from '~/composables/types/searchEngine'

const SCHEMA_AVAILABILITY = 'https://schema.org/InStock'

function absolutizeMediaUrl(path: string, siteBase: string): string {
  if (!path) return ''
  if (/^https?:\/\//i.test(path)) return path
  try {
    const normalized = path.startsWith('/') ? path : `/${path}`
    return new URL(normalized, siteBase).href
  } catch {
    return path
  }
}

function buildProductNode(
  hit: SearchEngineEventItem,
  resolveKitProductUrl: (permalink: string) => string,
  siteBase: string
): Record<string, unknown> {
  const imageRaw = searchEngineEventGetters.getPrincipalImage(hit)
  const image = absolutizeMediaUrl(imageRaw, siteBase)
  const permalink = searchEngineEventGetters.fnGetPermalink(hit)
  const priceCents = searchEngineEventGetters.fnGetMinimumPriceAmount(hit)
  const priceStr = (priceCents / 100).toFixed(2)
  const currency =
    searchEngineEventGetters.fnGetMinimumPriceCurrency(hit) || 'EUR'

  const product: Record<string, unknown> = {
    '@type': 'Product',
    name: searchEngineEventGetters.fnGetTitle(hit),
    url: resolveKitProductUrl(permalink),
    offers: {
      '@type': 'Offer',
      price: priceStr,
      priceCurrency: currency,
      availability: SCHEMA_AVAILABILITY,
    },
  }

  if (image) {
    product.image = image
  }

  const reviewCount = searchEngineEventGetters.getRatingsAmount(hit)
  const ratingValue = searchEngineEventGetters.getAverageRating(hit, 'en-US')
  if (
    reviewCount > 0 &&
    ratingValue != null &&
    ratingValue !== '' &&
    ratingValue !== 0 &&
    ratingValue !== '0'
  ) {
    product.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: ratingValue,
      reviewCount,
      bestRating: 5,
      worstRating: 1,
    }
  }

  return product
}

/**
 * JSON-LD @graph of Product entities for the DIY kits browse page (SSR + client pagination).
 * Same shape as useKitProductSchema; avoids ItemList/ListItem edge cases in consumers.
 */
export function useKitsBrowseProductGraphSchema(
  hits: ComputedRef<SearchEngineEventItem[]> | Ref<SearchEngineEventItem[]>
) {
  const localePath = useLocalePath()
  const {
    public: { baseUrl },
  } = useRuntimeConfig()
  const siteBase = baseUrl as string

  /** Must not call useFullLocalizedUrl in useHead resolver: that helper invokes useSubdomain() on every call. */
  const resolveKitProductUrl = (permalink: string) => {
    const localized = localePath(`/k/${permalink}/`)
    return withTrailingSlash(new URL(localized, siteBase).toString(), true)
  }

  useHead(() => {
    const list = unref(hits)
    const graph = list.map((hit) =>
      buildProductNode(hit, resolveKitProductUrl, siteBase)
    )

    const jsonLd = {
      '@context': 'https://schema.org',
      '@graph': graph,
    }

    return {
      script: [
        {
          key: 'kits-browse-product-graph-schema',
          type: 'application/ld+json',
          innerHTML: JSON.stringify(jsonLd),
          tagPriority: 2,
        },
      ],
    }
  })
}
